import { useEffect, useRef, useState } from 'react';
import { useAuthStore } from '@/stores/auth';
import { api } from '@/lib/api';
import { isSupplierPortalUser } from '@/lib/supplier-portal-user';

interface SSOCallbackPageProps {
  onNavigate: (path: string) => void;
}

export function SSOCallbackPage({ onNavigate }: SSOCallbackPageProps) {
  const [error, setError] = useState<string | null>(null);
  const loadUser = useAuthStore((s) => s.loadUser);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const run = async () => {
      const hash = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : window.location.hash;
      const search = window.location.search.startsWith('?') ? window.location.search.slice(1) : window.location.search;
      const hashParams = new URLSearchParams(hash.startsWith('/') ? hash.split('?')[1] ?? '' : hash);
      const searchParams = new URLSearchParams(search);
      const accessToken = hashParams.get('access_token') || searchParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token') || searchParams.get('refresh_token');

      if (!accessToken || !refreshToken) {
        setError('Token fehlen. Bitte melden Sie sich erneut an.');
        return;
      }

      try {
        window.history.replaceState({}, document.title, `${window.location.pathname}#/auth/callback`);
        localStorage.setItem('access_token', accessToken);
        localStorage.setItem('refresh_token', refreshToken);
        api.setToken(accessToken);
        useAuthStore.setState({ user: null, isAuthenticated: false });
        await loadUser();
        if (!useAuthStore.getState().isAuthenticated || !useAuthStore.getState().user) {
          throw new Error('Session could not be loaded');
        }
        const roles = useAuthStore.getState().user?.roles as string[] | undefined;
        onNavigate(isSupplierPortalUser(roles) ? '/supplier-portal' : '/dashboard');
      } catch {
        api.setToken(null);
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        setError('Anmeldung konnte nicht abgeschlossen werden.');
      }
    };

    run();
  }, [loadUser, onNavigate]);

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4">
        <p className="mb-4 text-center text-danger">{error}</p>
        <button
          type="button"
          className="rounded-lg bg-primary px-4 py-2 text-primary-foreground hover:bg-primary-hover"
          onClick={() => onNavigate('/auth/login')}
        >
          Zur Anmeldung
        </button>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4">
      <div className="h-10 w-10 animate-spin rounded-full border-4 border-border border-t-accent" />
      <p className="mt-4 text-muted">Anmeldung wird abgeschlossen…</p>
    </div>
  );
}
